import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import Button from '../components/Button';
import Toast from '../components/Toast';
import { authApi } from '../api';
import { MAGIC_RESEND_COOLDOWN_MS } from '../constants';
import { useNextParam } from '../lib/useNextParam';

const COPY_TITLE = 'Check your email';
const COPY_SUBTITLE = 'We sent a sign-in link to your inbox. Click it to continue.';
const COPY_RESEND = 'Resend link';
const COPY_RESEND_COOLDOWN = 'You can resend the link in';
const COPY_RESEND_OK = 'Sent. Check your inbox again.';
const SECOND_MS = 1000;

export default function MagicLink() {
  const [params] = useSearchParams();
  const next = useNextParam();
  const email = params.get('email') ?? '';
  const [cooldown, setCooldown] = useState(MAGIC_RESEND_COOLDOWN_MS);
  const [resending, setResending] = useState(false);
  const [resentNotice, setResentNotice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((current) => Math.max(0, current - SECOND_MS));
    }, SECOND_MS);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  async function onResend() {
    setError(null);
    setResending(true);
    setResentNotice(false);
    try {
      const result = await authApi.magic(email, next);
      if ('ok' in result && result.ok) {
        setCooldown(result.cooldown_ms);
        setResentNotice(true);
        return;
      }
      setError(result.message);
    } finally {
      setResending(false);
    }
  }

  const remainingSeconds = Math.ceil(cooldown / SECOND_MS);
  const cooldownActive = cooldown > 0;

  return (
    <AuthShell title={COPY_TITLE} subtitle={COPY_SUBTITLE}>
      <p className="mb-4 text-sm text-dark-secondary">
        Sent to <strong>{email}</strong>.
      </p>
      {resentNotice ? (
        <div className="mb-4">
          <Toast variant="success">{COPY_RESEND_OK}</Toast>
        </div>
      ) : null}
      {error ? (
        <div className="mb-4">
          <Toast variant="error">{error}</Toast>
        </div>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        onClick={onResend}
        disabled={cooldownActive}
        loading={resending}
        className="w-full"
      >
        {cooldownActive ? `${COPY_RESEND_COOLDOWN} ${remainingSeconds}s` : COPY_RESEND}
      </Button>
    </AuthShell>
  );
}
